import type ReorderAnnouncer from "discourse/ui-kit/d-reorderable-list/-internals/coordinators/reorder-announcer";
import type {
  MoveTarget,
  ReorderableGroupApi,
  ReorderableGroupMember,
  ReorderableMove,
  Row,
} from "discourse/ui-kit/d-reorderable-list/types";

/** The arguments a move reads, resolved per call rather than captured. */
interface MoveEngineArgs<T> {
  items: readonly T[];
  disabled?: boolean;
  group?: ReorderableGroupApi<T>;
  onMove?: (move: ReorderableMove<T>) => void | false;
  listLabel?: string;
  spill?: boolean;
}

interface MoveEngineOptions<T> {
  /** The list's current arguments; never a snapshot. */
  args: () => MoveEngineArgs<T>;

  /** The list's current row projection, resolved at commit time. */
  rows: () => Row<T>[];

  /** The list's group id, or its standalone default. */
  listId: () => string;

  /** What speaks the result. */
  announcer: ReorderAnnouncer<T>;

  /**
   * Returns focus to a row's handle after a commit moved it in the DOM. Owned
   * by the component rather than here, because it resolves against the list
   * element the keyboard modifier installed on.
   */
  refocusIndex: (index: number) => void;
}

/**
 * The move algebra: every committed reorder, in-list and cross-list alike.
 *
 * Owns the single commit chokepoint. Both input methods and both drag paths
 * funnel into `commitSeqMove`, which calls the consumer back once and
 * announces once, and neither for a move that lands where it started.
 */
export default class MoveEngine<T> {
  #args: () => MoveEngineArgs<T>;
  #rows: () => Row<T>[];
  #listId: () => string;
  #announcer: ReorderAnnouncer<T>;
  #refocusIndex: (index: number) => void;

  constructor({
    args,
    rows,
    listId,
    announcer,
    refocusIndex,
  }: MoveEngineOptions<T>) {
    this.#args = args;
    this.#rows = rows;
    this.#listId = listId;
    this.#announcer = announcer;
    this.#refocusIndex = refocusIndex;
  }

  /**
   * The in-list move both the menu and the chord funnel into: one step or one
   * jump within the movable subsequence, then focus back onto the handle the
   * move just displaced.
   *
   * Resolved fresh by key rather than acting on the row object the render
   * captured, because a move commits against the list as it stands now.
   *
   * A move that would leave the list past either end is refused, and the
   * refusal is announced. Reaching an end is information, and a silent no-op is
   * the failure this component exists to stop repeating. With `spill` the menu
   * offers the boundary step too, so both paths arrive here at an end; only
   * the spoken refusal is accelerator-only, the menu never offering a
   * destination that goes nowhere.
   *
   * @param key - The row to move.
   * @param target - Where to move it.
   * @param method - Which input method asked.
   */
  move(key: string, target: MoveTarget, method: "menu" | "keyboard") {
    const rows = this.#rows();
    const row = rows.find((candidate) => candidate.key === key);
    if (!row?.movable) {
      return;
    }

    const seq = rows.filter((candidate) => candidate.movable);
    const from = seq.indexOf(row);
    const to = {
      up: from - 1,
      down: from + 1,
      top: 0,
      bottom: seq.length - 1,
    }[target];

    if (to < 0 || to >= seq.length || to === from) {
      if (!this.#spill(key, target, method)) {
        this.#announcer.announceBoundary(row, target);
      }
      return;
    }

    this.#announcer.noteRun(key, method);
    const committed = this.commitSeqMove(method, rows, seq, from, to);
    // By landing index, not key: on a position-keyed list the old key now
    // names whichever row took the vacated slot.
    if (committed) {
      this.#announcer.updateRun(committed);
      this.#refocusIndex(committed.toIndex);
    }
  }

  /**
   * The member a row leaving this list in a direction would travel into, if
   * this list spills at all and there is a member that way.
   *
   * Read by the menu as well as by the move, so the destination it offers and
   * the destination a chord reaches are the same question asked once.
   *
   * @param target - Where the row was asked to go.
   */
  spillTarget(target: MoveTarget): ReorderableGroupMember | undefined {
    const { group, spill } = this.#args();
    // Only the two steps spill. "To top" and "to bottom" name this list's own
    // ends by definition, so there is no sense in which they overshoot.
    if (!spill || !group || (target !== "up" && target !== "down")) {
      return undefined;
    }
    // Between members "up" and "down" are not fixed directions, so the
    // crossing is asked for in reading order; `spill` says the two coincide.
    return group.neighbour(
      this.#listId(),
      target === "down" ? "next" : "previous"
    );
  }

  /**
   * The single commit both input methods funnel into: splices the move within
   * the movable subsequence, re-interleaves it with the frozen rows (which
   * keep their exact visible indices), suppresses no-ops, calls `@onMove`
   * once, and announces once. Returns the committed move, or `null` for a
   * no-op, so a caller can address the row by where it landed.
   *
   * @param method - Which input method asked for the move.
   * @param rows - The current row projection.
   * @param seq - The movable rows, in visible order.
   * @param from - The item's index within `seq`.
   * @param to - The destination index within `seq`.
   */
  commitSeqMove(
    method: ReorderableMove<T>["method"],
    rows: Row<T>[],
    seq: Row<T>[],
    from: number,
    to: number
  ): ReorderableMove<T> | null {
    const move = this.#buildSeqMove(method, rows, seq, from, to);
    if (move) {
      this.#finalize(move);
    }
    return move;
  }

  /**
   * The destination half of a cross-list move: resolves the source member
   * through the group, asks it for its removal projection, splices the item
   * into this list's visible order, and finalizes. A source member that
   * unregistered mid-drag, or a key that no longer resolves there, refuses
   * the drop. A disabled destination refuses every path at this chokepoint.
   *
   * @param sourceListId - The group listId the payload named as its origin.
   * @param key - The dragged row's key in the source member.
   * @param toIndex - The visible landing index in this list.
   * @param method - Which input method asked for the move.
   */
  commitCrossMove(
    sourceListId: string | undefined,
    key: string,
    toIndex: number,
    method: ReorderableMove<T>["method"]
  ): ReorderableMove<T> | null {
    const { disabled, group, items: toItems } = this.#args();
    if (disabled || !group || !sourceListId) {
      return null;
    }
    const member = group.lookupMember(sourceListId);
    const removal = member?.removalProjection(key);
    if (!member || !removal) {
      return null;
    }

    // The same slot model as everywhere else: frozen destination rows keep
    // their exact visible indices while the list grows by one slot, and the
    // arriving item joins the movable subsequence at the requested position.
    const rows = this.#rows();
    const item = removal.item as T;
    const size = rows.length + 1;
    const empty = Symbol("empty");
    const proposedTo: (T | typeof empty)[] = new Array(size).fill(empty);
    for (const row of rows) {
      if (!row.movable) {
        proposedTo[row.index] = row.item;
      }
    }
    const seqInsert = rows.filter(
      (row) => row.movable && row.index < toIndex
    ).length;
    const queue = rows.filter((row) => row.movable).map((row) => row.item);
    queue.splice(seqInsert, 0, item);
    let cursor = 0;
    let landingIndex = -1;
    for (let index = 0; index < size; index++) {
      if (proposedTo[index] === empty) {
        if (cursor === seqInsert) {
          landingIndex = index;
        }
        proposedTo[index] = queue[cursor++]!;
      }
    }

    const move: ReorderableMove<T> = {
      method,
      item,
      fromList: sourceListId,
      toList: this.#listId(),
      fromIndex: removal.fromIndex,
      toIndex: landingIndex,
      fromItems: member.getItems() as readonly T[],
      toItems,
      proposedFromItems: removal.proposedFromItems as readonly T[],
      proposedToItems: proposedTo as readonly T[],
    };
    this.#finalize(move);
    return move;
  }

  /** Speaks when an explicit cross-list destination can no longer accept. */
  announceRefusal(key: string) {
    const row = this.#rows().find((candidate) => candidate.key === key);
    if (row) {
      this.#announcer.announceRefusal(row);
    }
  }

  /**
   * What this list would look like with one row taken out of it, handed to the
   * group so a destination member can resolve a cross-list drop against the
   * source as it stands at drop time rather than a snapshot.
   *
   * @param key - The row leaving this list.
   * @returns The projection, or `undefined` when the row cannot leave.
   */
  removalProjection(key: string) {
    const rows = this.#rows();
    const moved = rows.find((candidate) => candidate.key === key);
    if (!moved?.movable) {
      return undefined;
    }
    // The same slot model as an in-list move: frozen rows keep their visible
    // indices, the list shrinks by its last slot, and a frozen row that sat
    // on that slot joins the end of the refill queue.
    const size = rows.length - 1;
    const empty = Symbol("empty");
    const proposed: (T | typeof empty)[] = new Array(size).fill(empty);
    const overflow: T[] = [];
    for (const row of rows) {
      if (!row.movable) {
        if (row.index < size) {
          proposed[row.index] = row.item;
        } else {
          overflow.push(row.item);
        }
      }
    }
    const queue = rows
      .filter((row) => row.movable && row.key !== key)
      .map((row) => row.item)
      .concat(overflow);
    let cursor = 0;
    for (let index = 0; index < size; index++) {
      if (proposed[index] === empty) {
        proposed[index] = queue[cursor++]!;
      }
    }
    return {
      item: moved.item,
      fromIndex: moved.index,
      proposedFromItems: proposed as readonly T[],
    };
  }

  /**
   * Hands a row refused at this list's end to the adjacent member.
   *
   * @param key - The row that ran out of room.
   * @param target - The direction it was pushed.
   * @param method - Which input method asked, carried across so the consumer
   *   is told what the reader actually did.
   * @returns Whether it went, which is what decides whether the refusal is
   *   spoken instead.
   */
  #spill(
    key: string,
    target: MoveTarget,
    method: "menu" | "keyboard"
  ): boolean {
    const member = this.spillTarget(target);
    if (!member) {
      return false;
    }
    // Entering from above lands first and entering from below lands last, so a
    // row keeps travelling the way it was pushed once it has crossed.
    const toIndex = target === "down" ? 0 : member.getItems().length;
    return !!member.acceptMove(this.#listId(), key, toIndex, method);
  }

  /**
   * Builds the normalized move for one step within the movable subsequence,
   * or `null` for a no-op.
   *
   * @param method - Which input method asked for the move.
   * @param rows - The current row projection.
   * @param seq - The movable rows, in visible order.
   * @param from - The item's index within `seq`.
   * @param to - The destination index within `seq`.
   */
  #buildSeqMove(
    method: ReorderableMove<T>["method"],
    rows: Row<T>[],
    seq: Row<T>[],
    from: number,
    to: number
  ): ReorderableMove<T> | null {
    if (to === from) {
      return null;
    }

    const moved = seq[from]!;
    const nextSeq = [...seq];
    nextSeq.splice(from, 1);
    nextSeq.splice(to, 0, moved);

    // Frozen rows keep their visible slots; the movable slots are refilled in
    // the new subsequence order.
    let cursor = 0;
    const proposed = rows.map((row) =>
      row.movable ? nextSeq[cursor++]!.item : row.item
    );
    const toIndex = seq[to]!.index;

    const { items } = this.#args();
    const listId = this.#listId();
    return {
      method,
      item: moved.item,
      fromList: listId,
      toList: listId,
      fromIndex: moved.index,
      toIndex,
      fromItems: items,
      toItems: items,
      proposedFromItems: proposed,
      proposedToItems: proposed,
    };
  }

  /**
   * The single exit for every committed move: routes the callback to the
   * group when the list is a member (its own `@onMove` otherwise), honors the
   * veto and the `@announceMove` override, and speaks exactly one
   * announcement — the cross-list variant when an item landed here from
   * another member and this list carries a `@listLabel`.
   *
   * @param move - The normalized move to report.
   */
  #finalize(move: ReorderableMove<T>) {
    if (!this.#dispatch(move)) {
      this.#announcer.cancelRun();
      return;
    }
    this.#announcer.announceMove(move);
  }

  /**
   * Reports a move to its callback owner — the group when the list is a
   * member, its own `@onMove` otherwise.
   *
   * @param move - The normalized move.
   * @returns Whether the move may be announced (`false` when vetoed).
   */
  #dispatch(move: ReorderableMove<T>): boolean {
    const handler = this.#args().group?.onMove ?? this.#args().onMove;
    return !!handler && handler(move) !== false;
  }
}
